/**
 * Player engine attachment.
 *
 * Which engine to use is decided by the **server-resolved per-item format**, never by
 * the source default and never by guessing from the URL. hls.js cannot play a bare
 * MPEG-TS byte stream and mpegts.js cannot play an HLS playlist, so a wrong choice here
 * is a black screen rather than a degraded experience.
 *
 * Each engine returns a `detach` so the player can tear one down and build another when
 * a session is re-minted.
 */
import type { PlayerEngine } from '@filmodasi/shared';

/**
 * Playback controls for an engine that does not drive the `<video>` element.
 *
 * Every engine but AVPlay pushes bytes into the media element, so the element itself is
 * the source of truth for position, duration and play state. AVPlay does not: it decodes
 * on a hardware plane the DOM cannot see, and the `<video>` element it sits over stays
 * empty and reports `currentTime` of 0 forever.
 *
 * Without this, resume, Continue Watching and the progress reports would all silently
 * record position zero on the one device where films are most likely to be watched to the
 * end. Present only when the engine needs it; absent means "ask the element".
 */
import { pickTrackByLanguage } from './trackPreference';
import { describeTracks, type LabelledTrack } from './trackLabel';
import type { AvplayTrackInfo } from '../device/useDeviceProfile';

export interface EngineControls {
  currentTime: () => number;
  durationSec: () => number | null;
  paused: () => boolean;
  play: () => void;
  pause: () => void;
  seek: (seconds: number) => void;
}

/**
 * The audio and subtitle tracks an engine can offer, and the state of the choice.
 *
 * `activeSubtitle` of null means "nothing is showing", which is a real answer and not an
 * absence — the menu ticks its "Kapalı" row for it. An engine that genuinely cannot say
 * what is selected does not expose {@link EngineTracks} at all, so there is no third state
 * to represent here and no way for the UI to accidentally render "off" as a guess.
 */
export interface TrackSet {
  audio: LabelledTrack[];
  subtitle: LabelledTrack[];
  activeAudio: string | null;
  activeSubtitle: string | null;
}

/**
 * Live track selection.
 *
 * **Present only where the engine can honestly do all three parts** — enumerate, switch
 * mid-playback, and report what is selected. `native`, `remux` and `mpegts` cannot:
 * `HTMLMediaElement.audioTracks` is unimplemented in Chrome and Firefox, so there is
 * nothing to list. Those engines leave this undefined and the menu says so, rather than
 * offering a list that is empty for a reason it declines to explain.
 */
export interface EngineTracks {
  get: () => TrackSet;
  /** `null` for subtitles means off. Passing an unknown id is ignored, not an error. */
  select: (kind: 'audio' | 'subtitle', id: string | null) => void;
  /**
   * Called whenever the list or the selection changes; returns an unsubscribe.
   *
   * Needed because the list does not exist yet when the engine attaches. hls.js populates
   * it at `MANIFEST_PARSED`, seconds later — a menu that read once at attach time would be
   * permanently empty on exactly the engine that supports this best.
   */
  subscribe: (listener: () => void) => () => void;
}

export interface AttachedEngine {
  detach: () => void;
  controls?: EngineControls;
  tracks?: EngineTracks;
}

/** Fans one notification out to every listener, ignoring throws from any of them. */
function emitter(): { emit: () => void; subscribe: (l: () => void) => () => void } {
  const listeners = new Set<() => void>();
  return {
    emit: () => {
      for (const l of [...listeners]) l();
    },
    subscribe: (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
  };
}

export interface AttachOptions {
  video: HTMLVideoElement;
  url: string;
  isLive: boolean;
  onError: (message: string) => void;
  /**
   * The profile's audio and subtitle language preferences, as ISO 639-1 codes.
   *
   * Applied where the engine can: hls.js exposes both track lists and lets them be
   * switched, so the preference is honoured there. Native playback — Safari's own HLS, and
   * a plain `<video src>` for MP4 — cannot be: `HTMLMediaElement.audioTracks` is
   * unimplemented in Chrome and Firefox, so there is nothing to enumerate. A stream played
   * that way keeps its own default track, which is the honest fallback and is why nothing
   * here reports failure.
   */
  preferredAudioLanguage?: string | null;
  preferredSubtitleLanguage?: string | null;
}

/**
 * Safari (and iOS in particular) plays HLS natively and refuses MSE for it, so hls.js
 * must not be used there even though it loads fine.
 */
function prefersNativeHls(video: HTMLVideoElement): boolean {
  return video.canPlayType('application/vnd.apple.mpegurl') !== '';
}

async function attachHls(opts: AttachOptions): Promise<AttachedEngine> {
  const { video, url, isLive, onError } = opts;

  if (prefersNativeHls(video)) {
    video.src = url;
    return { detach: () => video.removeAttribute('src') };
  }

  const { default: Hls } = await import('hls.js');
  if (!Hls.isSupported()) {
    onError('Bu tarayıcı HLS oynatmayı desteklemiyor');
    return { detach: () => {} };
  }

  const hls = new Hls({
    // The proxy already sits between us and the provider, so a long forward buffer just
    // multiplies VPS egress for content the viewer may never watch.
    maxBufferLength: isLive ? 20 : 60,
    // Live: stay near the edge, or a long session drifts minutes behind.
    liveSyncDurationCount: 3,
    enableWorker: true,
  });

  hls.on(Hls.Events.ERROR, (_e, data) => {
    if (!data.fatal) return;
    // hls.js can recover from a single network or media error; only give up after that.
    if (data.type === Hls.ErrorTypes.NETWORK_ERROR) hls.startLoad();
    else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) hls.recoverMediaError();
    else onError(`HLS hatası: ${data.details}`);
  });

  /*
   * Track selection waits for the manifest.
   *
   * `hls.audioTracks` is empty until then, and setting `hls.audioTrack` before the lists
   * exist is silently ignored — which looks exactly like the preference not working.
   * `MANIFEST_PARSED` is the first moment both lists are populated.
   */
  hls.on(Hls.Events.MANIFEST_PARSED, () => {
    const audio = pickTrackByLanguage(hls.audioTracks, opts.preferredAudioLanguage);
    if (audio !== null) hls.audioTrack = audio;

    const subtitle = pickTrackByLanguage(hls.subtitleTracks, opts.preferredSubtitleLanguage);
    // Only ever turned *on* by a preference. A viewer who has not asked for subtitles
    // should not have them appear because a stream happened to carry a matching track,
    // and `-1` (off) is what hls.js starts with.
    if (subtitle !== null) {
      hls.subtitleTrack = subtitle;
      hls.subtitleDisplay = true;
    }
  });

  /*
   * Live track selection.
   *
   * hls.js is the one engine that does all three parts properly, so the menu is at its
   * fullest here. Indices into `hls.audioTracks` / `hls.subtitleTracks` are used as ids
   * rather than the tracks' own `id` field: the setters are documented as taking an index,
   * and deriving the id from the same array the setter indexes into means the two cannot
   * disagree about which track "3" is.
   */
  const changed = emitter();
  for (const event of [
    Hls.Events.MANIFEST_PARSED,
    Hls.Events.AUDIO_TRACKS_UPDATED,
    Hls.Events.AUDIO_TRACK_SWITCHED,
    Hls.Events.SUBTITLE_TRACKS_UPDATED,
    Hls.Events.SUBTITLE_TRACK_SWITCH,
  ]) {
    hls.on(event, changed.emit);
  }

  const tracks: EngineTracks = {
    get: () => {
      const audio = describeTracks(
        hls.audioTracks.map((t, i) => ({ id: String(i), lang: t.lang, name: t.name })),
        'audio',
      );
      const subtitle = describeTracks(
        hls.subtitleTracks.map((t, i) => ({ id: String(i), lang: t.lang, name: t.name })),
        'subtitle',
      );
      return {
        audio,
        subtitle,
        activeAudio: hls.audioTrack >= 0 ? String(hls.audioTrack) : null,
        // `subtitleDisplay` is checked as well as the index: hls.js keeps the selected
        // track when display is switched off, so the index alone would tick a row while
        // nothing was on screen.
        activeSubtitle:
          hls.subtitleTrack >= 0 && hls.subtitleDisplay ? String(hls.subtitleTrack) : null,
      };
    },
    select: (kind, id) => {
      if (kind === 'audio') {
        const index = Number(id);
        if (Number.isInteger(index) && index >= 0 && index < hls.audioTracks.length) {
          hls.audioTrack = index;
        }
        return;
      }
      if (id === null) {
        // Both, not just the index: leaving `subtitleDisplay` on would keep rendering the
        // last track's cues, so "Kapalı" would visibly not turn them off.
        hls.subtitleTrack = -1;
        hls.subtitleDisplay = false;
      } else {
        const index = Number(id);
        if (!Number.isInteger(index) || index < 0 || index >= hls.subtitleTracks.length) return;
        hls.subtitleTrack = index;
        hls.subtitleDisplay = true;
      }
      // hls.js emits `SUBTITLE_TRACK_SWITCH` for a real switch but not for turning display
      // off, so the tick would lag one press behind without this.
      changed.emit();
    },
    subscribe: changed.subscribe,
  };

  hls.loadSource(url);
  hls.attachMedia(video);

  return {
    detach: () => {
      hls.destroy();
    },
    tracks,
  };
}

async function attachMpegts(opts: AttachOptions): Promise<AttachedEngine> {
  const { video, url, isLive, onError } = opts;
  const mpegts = (await import('mpegts.js')).default;

  if (!mpegts.isSupported()) {
    onError('Bu tarayıcı MPEG-TS oynatmayı desteklemiyor');
    return { detach: () => {} };
  }

  const player = mpegts.createPlayer(
    { type: 'mpegts', isLive, url },
    // A bare transport stream has no playlist to resynchronise from, so the built-in
    // stash has to be small or the stream drifts further behind with every stall.
    { enableStashBuffer: false, liveBufferLatencyChasing: isLive, lazyLoad: false },
  );

  player.on(mpegts.Events.ERROR, (type: string, detail: string) => {
    onError(`TS hatası: ${type} ${detail}`);
  });

  player.attachMediaElement(video);
  player.load();

  return {
    detach: () => {
      player.destroy();
    },
  };
}

function attachNative(opts: AttachOptions): AttachedEngine {
  opts.video.src = opts.url;
  return { detach: () => opts.video.removeAttribute('src') };
}

/**
 * The remuxed stream is an ordinary fragmented MP4, so the native element plays it.
 *
 * The only difference is seeking: the response is a live-generated pipe with no byte
 * index, so `video.currentTime = x` cannot work and `useRemuxSeek` re-requests the URL
 * with `?t=` instead. Nothing extra is needed here.
 */
function attachRemux(opts: AttachOptions): AttachedEngine {
  return attachNative(opts);
}

export async function attachEngine(
  engine: PlayerEngine,
  opts: AttachOptions,
): Promise<AttachedEngine> {
  switch (engine) {
    case 'hls':
      return attachHls(opts);
    case 'mpegts':
      return attachMpegts(opts);
    case 'native':
      return attachNative(opts);
    case 'remux':
      return attachRemux(opts);
    case 'avplay':
      return attachAvplay(opts);
  }
}

/* ------------------------------------------------------------------ *
 * AVPlay
 * ------------------------------------------------------------------ */

/**
 * The platform decoder, used for the containers Chromium refuses.
 *
 * ## Why it exists
 *
 * Most of this account's VOD library is Matroska. Chromium on the panel will not demux
 * it, so without AVPlay every one of those films goes through the server's remux path —
 * ffmpeg running on the VPS for the length of the film, and the bytes crossing it twice.
 * AVPlay decodes mkv, avi and much else directly on the television's own hardware, so the
 * proxy goes back to being a pipe.
 *
 * ## Why it cannot use the video element
 *
 * AVPlay draws on a hardware video plane *behind* the browser's page, not into the DOM.
 * The `<object>` element below does not display anything itself — it exists so the
 * platform has a node to associate the player with, and so the page reserves the space.
 * Making the area actually show the picture means punching a hole through everything
 * stacked above it, which is what the transparent background here is for.
 *
 * Coordinates are passed in CSS pixels of the page, which on a TV widget map 1:1 to the
 * panel, and are re-sent on resize because a rect set before layout settles lands in the
 * wrong place — a mistake that shows up as a video playing in a corner at quarter size.
 */
/**
 * Track enumeration and selection for an AVPlay stream.
 *
 * Built after `prepareAsync`, because that is the first moment `getTotalTrackInfo` returns
 * anything — before it the list is empty, and a selection made against it would silently
 * do nothing.
 *
 * Every call is wrapped: `getTotalTrackInfo` and `setSelectTrack` are optional on the
 * platform object (they differ across Tizen versions), the `extra_info` field is a JSON
 * *string* whose shape the panel does not guarantee, and a stream with one audio track
 * legitimately refuses a switch. None of that is worth failing playback over — the film
 * plays with its default track, which is exactly what happened before this existed. A
 * platform that refuses the whole thing returns `null` here and the menu says the engine
 * cannot list tracks, which is true.
 *
 * ## Why subtitles are conditional and audio is not
 *
 * Selecting a TEXT track on AVPlay displays nothing on its own. The decoder draws on a
 * hardware plane the DOM cannot reach, so cues are delivered to the app through
 * `onsubtitlechange` and the app draws them — but only after `setSilentSubtitle(false)`
 * tells the platform to stop swallowing them. Where that method is absent there is no way
 * to render a cue, so subtitles are left out of the list entirely rather than offered as a
 * control that appears to work.
 */
function createAvplayTrackLayer(
  avplay: NonNullable<Window['webapis']>['avplay'],
  onSubtitleCleared: () => void,
): { tracks: EngineTracks; applyPreferences: (opts: AttachOptions) => void } | null {
  // Captured into locals rather than used through `avplay.` inside the closure below:
  // TypeScript narrows the optional members here but loses that narrowing across the
  // function boundary, and a non-null assertion at the call site would be a claim rather
  // than a check.
  // `player` is a const, so its narrowing survives into the closure below; the parameter
  // itself does not, because TypeScript must assume a parameter can be reassigned.
  //
  // `unbound-method` is right to flag these two — `avplay` is a foreign platform object
  // and its methods may well use `this`. That is exactly why every call below goes through
  // `.call(player)` rather than invoking the detached reference. The rule cannot see that,
  // so it is silenced here and nowhere else in the codebase.
  const player = avplay;
  // eslint-disable-next-line @typescript-eslint/unbound-method -- invoked via .call(player)
  const getTracks = player?.getTotalTrackInfo;
  // eslint-disable-next-line @typescript-eslint/unbound-method -- invoked via .call(player)
  const selectTrack = player?.setSelectTrack;
  // eslint-disable-next-line @typescript-eslint/unbound-method -- invoked via .call(player)
  const silentSubtitle = player?.setSilentSubtitle;
  if (!player || !getTracks || !selectTrack) return null;

  let all: AvplayTrackInfo[];
  try {
    all = getTracks.call(player);
  } catch {
    return null;
  }

  /** `extra_info` is a JSON string on every panel checked, and prose on none of them. */
  const describe = (track: AvplayTrackInfo) => {
    let info: { language?: string; track_lang?: string; name?: string } = {};
    try {
      info = typeof track.extra_info === 'string' ? JSON.parse(track.extra_info) : {};
    } catch {
      /* leave it empty; the track falls back to a numbered label */
    }
    return {
      id: String(track.index),
      lang: info.language ?? info.track_lang ?? null,
      name: info.name ?? null,
    };
  };

  const rawAudio = all.filter((t) => t.type === 'AUDIO').map(describe);
  const rawText = silentSubtitle ? all.filter((t) => t.type === 'TEXT').map(describe) : [];

  const audio = describeTracks(rawAudio, 'audio');
  const subtitle = describeTracks(rawText, 'subtitle');
  if (audio.length === 0 && subtitle.length === 0) return null;

  /*
   * The selection is tracked here rather than read back from the platform.
   *
   * AVPlay has no "what is selected" call, so this is the only record of it — seeded to
   * null, which is the truth at attach time: the stream is playing whatever it defaults
   * to and nothing in this app chose it. The menu shows no tick until something does,
   * which is better than ticking a guess.
   */
  let activeAudio: string | null = null;
  let activeSubtitle: string | null = null;
  const changed = emitter();

  const apply = (type: 'AUDIO' | 'TEXT', index: number): boolean => {
    try {
      selectTrack.call(player, type, index);
      return true;
    } catch {
      // A stream with one track legitimately refuses the switch. The selection is left
      // where it was rather than recorded as changed, so the tick keeps telling the truth.
      return false;
    }
  };

  const setSilent = (silent: boolean) => {
    if (!silentSubtitle) return;
    try {
      silentSubtitle.call(player, silent);
    } catch {
      /* older platform; the cue callback simply never fires */
    }
  };

  const tracks: EngineTracks = {
    get: () => ({ audio, subtitle, activeAudio, activeSubtitle }),
    select: (kind, id) => {
      if (kind === 'audio') {
        if (id === null || !audio.some((t) => t.id === id)) return;
        if (apply('AUDIO', Number(id))) {
          activeAudio = id;
          changed.emit();
        }
        return;
      }
      if (id === null) {
        // There is no "select no TEXT track" on AVPlay, so off means: stop being handed
        // cues, and clear whatever is currently on screen. Without the second half the
        // last cue would sit there for the rest of the film.
        setSilent(true);
        onSubtitleCleared();
        activeSubtitle = null;
        changed.emit();
        return;
      }
      if (!subtitle.some((t) => t.id === id)) return;
      if (apply('TEXT', Number(id))) {
        setSilent(false);
        activeSubtitle = id;
        changed.emit();
      }
    },
    subscribe: changed.subscribe,
  };

  return {
    tracks,
    applyPreferences: (opts: AttachOptions) => {
      /*
       * Matched against the **raw** lists, not the labelled ones.
       *
       * `LabelledTrack` has no `name` — the track's own label has been folded into
       * `label` by then — so matching there would silently drop rules 3 and 4 of
       * `pickTrackByLanguage`, the two that read the human name. A stream whose Turkish
       * track is tagged nothing and merely *called* "Türkçe" would stop being selected,
       * and it would look exactly like the preference having no effect.
       */
      // Only where there is something to choose between: a single-track stream has no
      // preference to honour, and asking the platform to switch to the track it is
      // already playing is a call that can only fail.
      if (opts.preferredAudioLanguage && rawAudio.length > 1) {
        const hit = pickTrackByLanguage(rawAudio, opts.preferredAudioLanguage);
        if (hit !== null) tracks.select('audio', rawAudio[hit]!.id);
      }
      // Subtitles are only ever turned *on* by a preference, never off — a viewer who has
      // not asked for them should not get them because a stream happened to carry a match.
      //
      // One track is enough here, unlike audio: subtitles start off, so a single matching
      // track is still a change worth making. The old code required two for both and so
      // ignored the commonest case there is — one Turkish subtitle track and a viewer who
      // asked for Turkish subtitles.
      if (opts.preferredSubtitleLanguage && rawText.length > 0) {
        const hit = pickTrackByLanguage(rawText, opts.preferredSubtitleLanguage);
        if (hit !== null) tracks.select('subtitle', rawText[hit]!.id);
      }
    },
  };
}

async function attachAvplay(opts: AttachOptions): Promise<AttachedEngine> {
  const { video, url, isLive, onError } = opts;
  const avplay = window.webapis?.avplay;

  if (!avplay) {
    // The TV *browser* reports as a TV but has no platform APIs, and so does `?tv=1` on a
    // laptop. Falling back keeps both usable instead of failing where a plain element
    // would have worked.
    return attachNative(opts);
  }

  const host = document.createElement('object');
  host.setAttribute('type', 'application/avplayer');
  host.style.cssText =
    'position:absolute;inset:0;width:100%;height:100%;background:transparent;';
  video.parentElement?.insertBefore(host, video);
  // The element is left in the tree but emptied: the page layout, the fullscreen wrapper
  // and the control-bar positioning are all built around it.
  video.style.visibility = 'hidden';

  /*
   * The surface subtitles are drawn on.
   *
   * AVPlay decodes the picture on a hardware plane behind the page and never draws text
   * itself, so a selected TEXT track produces callbacks and nothing visible. This node is
   * where those cues go. It sits over the video area rather than inside `host`, which the
   * platform owns, and it is `aria-hidden` because a screen reader reading subtitles aloud
   * over the film's own audio is not a feature.
   *
   * Positioned at the bottom with a wide safe margin: the panel overscans, and a cue at
   * the true edge of a 1080p frame is partly off the screen on this television.
   */
  const cues = document.createElement('div');
  cues.className = 'avsub';
  cues.setAttribute('aria-hidden', 'true');
  video.parentElement?.insertBefore(cues, video);

  let cueTimer: number | null = null;
  const clearCue = () => {
    if (cueTimer !== null) window.clearTimeout(cueTimer);
    cueTimer = null;
    cues.textContent = '';
  };
  /**
   * `durationMs` is how long the platform says to keep the cue up, and it is not always
   * sane — zero and absurdly large values both turn up. A cue with no expiry would stay on
   * screen for the rest of the film, so the timer is clamped rather than trusted.
   */
  const showCue = (durationMs: number, text: string) => {
    if (cueTimer !== null) window.clearTimeout(cueTimer);
    // `textContent`, never `innerHTML`: this string comes from a third-party subtitle
    // track in a file this app did not produce.
    cues.textContent = text ?? '';
    const ms = Number.isFinite(durationMs) ? Math.min(Math.max(durationMs, 500), 10_000) : 4000;
    cueTimer = window.setTimeout(() => {
      cues.textContent = '';
      cueTimer = null;
    }, ms);
  };

  let position = 0;
  let duration: number | null = null;
  let paused = false;
  /**
   * Built after `prepareAsync`; null where the platform refuses to enumerate.
   *
   * Declared without an initialiser because every path that reaches the return below has
   * assigned it — the `catch` returns rather than falling through.
   */
  let layer: ReturnType<typeof createAvplayTrackLayer>;

  const applyRect = () => {
    const r = host.getBoundingClientRect();
    try {
      avplay.setDisplayRect(
        Math.round(r.left),
        Math.round(r.top),
        Math.round(r.width),
        Math.round(r.height),
      );
    } catch {
      /* refused before prepare; the call after prepare is the one that matters */
    }
  };

  try {
    avplay.open(url);
    avplay.setDisplayMethod?.('PLAYER_DISPLAY_MODE_LETTER_BOX');
    avplay.setListener({
      oncurrentplaytime(ms: number) {
        position = ms / 1000;
      },
      onstreamcompleted() {
        paused = true;
        // Mirrored onto the element so the rest of the app — progress flush on pause,
        // the next-episode roll-forward — sees an ordinary ended stream.
        video.dispatchEvent(new Event('ended'));
      },
      onerror(type: string) {
        onError(`AVPlay hatası: ${type}`);
      },
      onbufferingcomplete() {
        try {
          duration = avplay.getDuration() / 1000 || null;
        } catch {
          duration = null;
        }
      },
      // Registered here rather than added later because `setListener` replaces the whole
      // listener object — a second call to add this one would drop the four above it.
      onsubtitlechange(durationMs: number, text: string) {
        showCue(durationMs, text);
      },
    });

    applyRect();
    await new Promise<void>((resolve, reject) => {
      avplay.prepareAsync(
        () => resolve(),
        (e: unknown) => reject(e instanceof Error ? e : new Error(String(e))),
      );
    });
    // Again after prepare: the first call can be refused while the player is opening, and
    // the platform keeps whatever rect it had — usually the whole screen.
    applyRect();
    layer = createAvplayTrackLayer(avplay, clearCue);
    layer?.applyPreferences(opts);
    avplay.play();
  } catch (err) {
    onError(`AVPlay başlatılamadı: ${err instanceof Error ? err.message : String(err)}`);
    host.remove();
    cues.remove();
    video.style.visibility = '';
    return attachNative(opts);
  }

  window.addEventListener('resize', applyRect);

  return {
    detach() {
      window.removeEventListener('resize', applyRect);
      clearCue();
      try {
        avplay.stop();
        avplay.close();
      } catch {
        /* already torn down — nothing left to release */
      }
      host.remove();
      cues.remove();
      video.style.visibility = '';
    },
    // Safe to read by value: the layer is built inside the `try` above, which this line
    // is only reached after. `undefined` here means the platform refused to enumerate,
    // and the menu says exactly that.
    tracks: layer?.tracks,
    controls: {
      currentTime: () => position,
      durationSec: () => duration,
      paused: () => paused,
      play() {
        avplay.play();
        paused = false;
      },
      pause() {
        avplay.pause();
        paused = true;
      },
      seek(seconds) {
        // Live has no seekable window here; the platform throws rather than ignoring it.
        if (isLive) return;
        try {
          avplay.seekTo(Math.max(0, Math.round(seconds * 1000)));
          position = seconds;
        } catch {
          /* outside the seekable range — the position simply does not change */
        }
      },
    },
  };
}
